import { SystemEventTypeEnum, TEvent, TEventCallback, TEventWsCallback, TPlayerActionData, TWidget, TWInternalId } from '@libs/types';
import { ReplaySubject, Subject, Subscription } from 'rxjs';

class EventBus {
    private consoleColor = 'background: #222; color: #bada55';
    private consoleColorUnsubscribe = 'background: #222; color: #d25050';
    private consoleColorEmit = 'background: #222; color: #3fb64d';
    private eventBus: Subject<TEvent> = new ReplaySubject(
        Number(process.env.NX_PUBLIC_EVET_BUSS_BUFFER_SIZE ?? 10),
        Number(process.env.NX_PUBLIC_EVET_BUSS_BUFFER_TIME ?? 200),
    );
    private wsSubscription: Record<string, Subscription> = {};
    private subscriptions: Record<string, Subscription> = {};
    private getSubscriptionKey = (eventName: TEvent['type'], internalId: TWInternalId) => `${eventName}_${internalId}`;

    subscribeOnSpecificWidget(eventName: TEvent['type'], internalId: string, callback: TEventCallback): void {
        this.logSubscribe(`subscribe on SPECIFIC widget event: '${eventName}' internalId: '${internalId}'`);
        this.subscriptions[this.getSubscriptionKey(eventName, internalId)] = this.eventBus.subscribe((event: TEvent) => {
            if (event.type === eventName && event.payload.internalId === internalId) {
                callback(event.payload.value);
            }
        });
    }

    subscribe(eventName: TEvent['type'], widgetUid: string, callback: TEventCallback): void {
        this.logSubscribe(`subscribe on GENERAL event: '${eventName}'`);
        this.subscriptions[this.getSubscriptionKey(eventName, widgetUid)] = this.eventBus.subscribe((event: TEvent) => {
            if (event?.type === eventName) {
                callback(event.payload.value);
            }
        });
    }

    subscribeWS(internalId: string, callback: TEventWsCallback): void {
        this.logSubscribe(`subscribe on WS event`);
        this.wsSubscription[this.getSubscriptionKey(SystemEventTypeEnum.WS, internalId)] = this.eventBus.subscribe((event: TEvent) => {
            if (event?.type === SystemEventTypeEnum.WS && event.payload.internalId === internalId) {
                callback(event.payload.widget);
            }
        });
    }

    unsubscribeSpecificWidget(eventName: TEvent['type'], internalId: TWInternalId): void {
        this.logUnsubscribe(`unsubscribeSpecificWidget: ${this.getSubscriptionKey(eventName, internalId)}`);
        this.subscriptions?.[this.getSubscriptionKey(eventName, internalId)]?.unsubscribe();
        delete this.subscriptions?.[this.getSubscriptionKey(eventName, internalId)];
    }

    unsubscribeWsSpecificWidget(internalId?: TWInternalId): void {
        if (internalId) {
            this.logUnsubscribe(`unsubscribeSpecificWidget: ${this.getSubscriptionKey(SystemEventTypeEnum.WS, internalId)}`);
            this.wsSubscription?.[this.getSubscriptionKey(SystemEventTypeEnum.WS, internalId)]?.unsubscribe();
            delete this.wsSubscription?.[this.getSubscriptionKey(SystemEventTypeEnum.WS, internalId)];
        }
    }

    unsubscribe(eventName: TEvent['type'], widgetUid: string): void {
        this.logUnsubscribe(`unsubscribe: ${this.getSubscriptionKey(eventName, widgetUid)}`);
        this.subscriptions?.[this.getSubscriptionKey(eventName, widgetUid)]?.unsubscribe();
        delete this.subscriptions?.[this.getSubscriptionKey(eventName, widgetUid)];
    }

    emit(eventName: TEvent['type'], internalId?: TWInternalId, data?: TPlayerActionData): void {
        if (internalId) {
            const event: TEvent = {
                type: eventName,
                payload: {
                    value: data,
                    internalId: internalId ?? '',
                },
            };
            this.logEmit(`Emitted event '${this.getSubscriptionKey(eventName, internalId)}' value: '${data}'`);
            this.eventBus.next(event);
        }
    }

    emitWsMessage(widget: TWidget<unknown>) {
        const event: TEvent = {
            type: SystemEventTypeEnum.WS,
            payload: {
                widget,
                internalId: widget?.internalId ?? '',
            },
        };
        this.logEmit(`Emitted WS event, JSON: '${widget}'`);
        this.eventBus.next(event);
    }

    emitGeneral(eventName: TEvent['type'], data?: TPlayerActionData): void {
        const event: TEvent = {
            type: eventName,
            payload: {
                value: data,
            },
        };
        this.logEmit(`Emitted general event '${eventName}' value: '${data}'`);
        this.eventBus.next(event);
    }

    private logEmit = (message: string) => {
        if (process.env.NX_PUBLIC_LOG_EVENT_EMIT) {
            console.log(`%c ${message}`, this.consoleColorEmit);
        }
    };
    private logUnsubscribe = (message: string) => {
        if (process.env.NX_PUBLIC_LOG_EVENT_UNSUBSCRIBE) {
            console.log(`%c ${message}`, this.consoleColorUnsubscribe);
        }
    };
    private logSubscribe = (message: string) => {
        if (process.env.NX_PUBLIC_LOG_EVENT_SUBSCRIBE) {
            console.log(`%c ${message}`, this.consoleColor);
        }
    };
}

const eventBus = new EventBus();

export { eventBus };
